import {
  findVaultInfoById,
  muldiv,
  SUPPLY_POOL_STRATEGY_INFOS,
  VaultInfo as VaultInfo_,
} from '@kunalabs-io/kai'
import { Amount } from '@kunalabs-io/kai'
import { Vault as VaultState } from '../../gen/kai-sav/vault/structs'
import { QueryObserverResult, RefetchOptions } from '@tanstack/react-query'
import { PhantomTypeArgument } from '../../gen/_framework/reified'
import { calcContinuousApy } from '../../util'
import { useObject, useObjectDataBatch } from '../../hooks/batshitObjectFetcher'
import { VAULTS } from '@kunalabs-io/kai'
import { calcTotalAvailableBalance } from '@kunalabs-io/kai'
import { useCallback, useMemo } from 'react'

const REFRESH_INTERVAL = 30000 // 30 seconds

export interface VaultUtilization {
  utilization: number
  maxWithdrawable: Amount
}

export function useVaultUtilizations() {
  const supplyPoolDatas = useObjectDataBatch(
    Object.values(SUPPLY_POOL_STRATEGY_INFOS).map(info => ({
      id: info.supplyPoolInfo.id,
      fromBcs: (bcs: Uint8Array) => info.supplyPoolInfo.fromBcs(bcs),
    })),
    {
      refetchInterval: REFRESH_INTERVAL,
    }
  )
  const vaultDatas = useObjectDataBatch(
    Object.values(VAULTS).map(info => ({
      id: info.id,
      fromBcs: (bcs: Uint8Array) => info.reified.fromBcs(bcs),
    })),
    {
      refetchInterval: REFRESH_INTERVAL,
    }
  )

  const findVaultIdForSupplyPool = useCallback((supplyPoolId: string) => {
    const vaultId = Object.values(SUPPLY_POOL_STRATEGY_INFOS).find(
      info => info.supplyPoolInfo.id === supplyPoolId
    )?.vault.id

    if (!vaultId) {
      throw new Error(`Vault not found for supply pool: ${supplyPoolId}`)
    }

    return vaultId
  }, [])

  const isLoading = supplyPoolDatas.some(d => d.isLoading) || vaultDatas.some(d => d.isLoading)

  const utilizations = useMemo(() => {
    const utilizations = new Map<string, VaultUtilization>()

    // Use a minute-aligned timestamp to avoid frequent re-renders
    const now = Math.floor(Date.now() / 60000) * 60000

    if (isLoading) {
      return utilizations
    }

    for (const supplyPoolData of supplyPoolDatas) {
      if (supplyPoolData.data) {
        const vaultId = findVaultIdForSupplyPool(supplyPoolData.data.id)
        const vaultData = vaultDatas.find(d => d.data?.id === vaultId)?.data
        if (!vaultData) {
          throw new Error(`Vault not found for supply pool: ${supplyPoolData.data.id}`)
        }
        const totalAvailableBalance = calcTotalAvailableBalance(vaultData, now)
        const locked = supplyPoolData.data.calcUpdatedTotalLiabilitiesX64(now) >> 64n

        const vaultInfo = findVaultInfoById(vaultId)
        if (!vaultInfo) {
          throw new Error(`Vault not found: ${vaultId}`)
        }

        const utilization = Number(locked) / Number(totalAvailableBalance)
        const maxWithdrawable = vaultInfo.T.newAmount(totalAvailableBalance - locked)

        utilizations.set(vaultId, {
          utilization,
          maxWithdrawable,
        })
      }
    }

    return utilizations
  }, [isLoading, supplyPoolDatas, vaultDatas])

  return {
    isLoading,
    utilizations,
  }
}

export interface UseVaultInfoOptions {
  refetchInterval?: number
}

export interface VaultInfo<T extends PhantomTypeArgument, YT extends PhantomTypeArgument> {
  vault?: VaultState<T, YT>
  tvl?: Amount
  tvlCap?: Amount
  apy?: number
  apr?: number
  performanceFee?: number
  strategyAccessIds?: string[]
  utilization?: number
  maxWithdrawable?: Amount
  isLoading: boolean
  refetch: (options?: RefetchOptions | undefined) => Promise<
    QueryObserverResult<
      {
        id: string
        bcs: Uint8Array
      },
      Error
    >
  >
}

export function calcVaultInfoFromData<
  T extends PhantomTypeArgument,
  YT extends PhantomTypeArgument,
>(vaultObj: VaultState<T, YT>) {
  const vault = findVaultInfoById(vaultObj.id)
  if (!vault) {
    throw new Error(`Vault not found: ${vaultObj.id}`)
  }

  let tvl: Amount | undefined
  if (vaultObj) {
    let tvlInt = vaultObj.freeBalance.value
    for (const strategy of vaultObj.strategies.contents) {
      tvlInt += strategy.value.borrowed
    }
    tvl = Amount.fromInt(tvlInt, vault.T.decimals)
  }

  let apy: number | undefined
  let apr: number | undefined
  let performanceFee: number | undefined
  if (vaultObj && tvl !== undefined) {
    const now = new Date().getTime() / 1000
    let unlockPerSecond = muldiv(
      vaultObj.timeLockedProfit.unlockPerSecond,
      10000n - vaultObj.performanceFeeBps,
      10000n
    )

    if (now > vaultObj.timeLockedProfit.finalUnlockTsSec) {
      unlockPerSecond = 0n
    }

    const unlockPerYear = unlockPerSecond * 60n * 60n * 24n * 365n
    if (tvl.int > 0n) {
      // caulcate average for BTC vaults because of satoshi rounding
      if (['wBTC', 'LBTC', 'xBTC'].includes(vault.T.symbol)) {
        const unlockPerSecond =
          Number(vaultObj.timeLockedProfit.unlockPerSecond) *
          (1 - Number(vaultObj.performanceFeeBps) / 10000)
        const unlockDurationSec = Number(
          vaultObj.timeLockedProfit.finalUnlockTsSec - vaultObj.timeLockedProfit.unlockStartTsSec
        )
        const normUnlockPerSecond =
          (unlockPerSecond * Math.min(unlockDurationSec, 30 * 60)) / (30 * 60)

        const unlockPerYear = normUnlockPerSecond * 60 * 60 * 24 * 365

        apr = Number(unlockPerYear) / Number(tvl.int)
      } else {
        apr = Number(unlockPerYear) / Number(tvl.int)
      }

      apy = calcContinuousApy(apr, 365)
    } else {
      apy = 0
      apr = 0
    }
    performanceFee = Number(vaultObj.performanceFeeBps) / 10000
  }

  let tvlCap: Amount | undefined
  if (vaultObj && vaultObj.tvlCap) {
    tvlCap = Amount.fromInt(vaultObj.tvlCap, vault.T.decimals)
  }

  let strategyAccessIds: string[] | undefined
  if (vaultObj) {
    strategyAccessIds = vaultObj.strategies.contents.map(s => s.key)
  }

  return {
    tvl,
    tvlCap,
    apy,
    apr,
    performanceFee,
    strategyAccessIds,
  }
}

export function useVaultInfo<T extends PhantomTypeArgument, YT extends PhantomTypeArgument>(
  vault: VaultInfo_<T, YT>,
  options?: UseVaultInfoOptions
): VaultInfo<T, YT> {
  const {
    data: vaultObj,
    isLoading,
    refetch,
  } = useObject({
    id: vault.id,
    fromBcs: (bcs: Uint8Array) => vault.reified.fromBcs(bcs),
    options,
  })
  const { utilization, maxWithdrawable } = useVaultUtilizations().utilizations.get(vault.id) || {}

  if (!vaultObj) {
    return {
      isLoading,
      refetch,
    }
  }

  const info = calcVaultInfoFromData(vaultObj)

  return {
    vault: vaultObj,
    ...info,
    utilization,
    maxWithdrawable,
    isLoading,
    refetch,
  }
}

export function useVaultInfos(
  vaults: VaultInfo_<PhantomTypeArgument, PhantomTypeArgument>[],
  options?: UseVaultInfoOptions
): VaultInfo<PhantomTypeArgument, PhantomTypeArgument>[] {
  const vaultDatas = useObjectDataBatch(
    vaults.map(vault => ({
      id: vault.id,
      fromBcs: (bcs: Uint8Array) => vault.reified.fromBcs(bcs),
      options,
    }))
  )

  const utilizations = useVaultUtilizations().utilizations

  const vaultInfos = vaultDatas.map(vaultData => {
    if (!vaultData.data) {
      return {
        isLoading: vaultData.isLoading,
        refetch: vaultData.refetch,
      }
    }

    const info = calcVaultInfoFromData(vaultData.data)

    return {
      vault: vaultData.data,
      ...info,
      utilization: utilizations.get(vaultData.data.id)?.utilization,
      maxWithdrawable: utilizations.get(vaultData.data.id)?.maxWithdrawable,
      isLoading: vaultData.isLoading,
      refetch: vaultData.refetch,
    }
  })

  return vaultInfos
}
